@if (isOpen()) {
  <aside
    class="fixed bottom-6 right-6 z-[60] flex h-[min(46rem,calc(100vh_-_8rem))] w-[min(32rem,calc(100vw_-_3rem))] flex-col overflow-hidden rounded-2xl border border-sk-border bg-sk-surface text-sk-text shadow-xl max-sm:inset-0 max-sm:h-[100dvh] max-sm:w-full max-sm:rounded-none max-sm:border-0"
    role="complementary"
    [attr.aria-label]="labels.panelName"
    (dragover)="onDragOver($event)"
    (dragleave)="onDragLeave($event)"
    (drop)="onDrop($event)"
  >
    @if (isDragging()) {
      <div class="pointer-events-none absolute inset-0 z-30 grid place-items-center border-2 border-dashed border-sk-primary bg-sk-surface/85 text-sm font-semibold text-sk-text">
        {{ labels.dropHere }}
      </div>
    }
    <header class="flex items-center gap-2 border-b border-sk-border px-3.5 py-3">
      <span class="grid h-9 w-9 flex-none place-items-center rounded-[10px] bg-status-primary-bg text-status-primary-fg" aria-hidden="true">
        <sk-icon name="bot" size="md" />
      </span>
      <div class="min-w-0 flex-1">
        <h2 class="text-[15px] font-semibold leading-tight">{{ labels.title }}</h2>
        <p class="flex items-center gap-1.5 text-xs text-sk-text-secondary" role="status">
          <span
            class="inline-block h-[7px] w-[7px] rounded-full"
            [class.bg-status-success-fg]="chat.connectionStatus() === 'connected' || chat.connectionStatus() === 'disconnected'"
            [class.bg-status-warning-fg]="chat.connectionStatus() === 'connecting' || chat.isBusy()"
            [class.bg-status-error-fg]="chat.connectionStatus() === 'error'"
            aria-hidden="true"
          ></span>
          {{ chat.statusLabel() }}
        </p>
      </div>
      <sk-button variant="ghost" size="sm" [attr.aria-label]="labels.newConversation" [attr.title]="labels.newConversation" (skClick)="onNewConversation()">
        <sk-icon name="plus" size="sm" />
      </sk-button>
      <sk-button
        variant="ghost"
        size="sm"
        [attr.aria-label]="labels.history"
        [attr.title]="labels.history"
        [attr.aria-pressed]="historyOpen()"
        (skClick)="toggleHistory()"
      >
        <sk-icon name="history" size="sm" />
      </sk-button>
      <sk-button
        variant="ghost"
        size="sm"
        [attr.aria-label]="sound.enabled() ? labels.soundMute : labels.soundUnmute"
        [attr.title]="sound.enabled() ? labels.soundMute : labels.soundUnmute"
        (skClick)="sound.toggle()"
      >
        <sk-icon [name]="sound.enabled() ? 'volume-2' : 'volume-x'" size="sm" />
      </sk-button>
      <sk-button variant="ghost" size="sm" [attr.aria-label]="labels.closeChat" [attr.title]="labels.closeChat" (skClick)="onClose()">
        <sk-icon name="x" size="sm" />
      </sk-button>
    </header>

    @if (historyOpen()) {
      <section class="flex flex-1 flex-col gap-1 overflow-y-auto p-3" [attr.aria-label]="labels.history">
        <input
          type="search"
          class="mb-2 w-full rounded-[10px] border border-sk-border-strong bg-sk-surface px-3 py-2 text-sm text-sk-text focus-visible:outline-2 focus-visible:outline-sk-border-focus"
          [formControl]="historyQuery"
          [attr.aria-label]="labels.historySearch"
          [placeholder]="labels.historySearch"
        />
        @if (chat.conversations().length === 0) {
          <p class="p-4 text-center text-sm text-sk-text-secondary">{{ labels.historyEmpty }}</p>
        } @else if (historyGroups().length === 0) {
          <p class="p-4 text-center text-sm text-sk-text-secondary">{{ labels.historyNoMatch }}</p>
        }
        @for (group of historyGroups(); track group.label) {
          <h3 class="mx-1 mb-1 mt-3 text-[11.5px] font-bold uppercase tracking-wider text-sk-text-secondary">{{ group.label }}</h3>
          <ul class="flex flex-col gap-1">
            @for (conversation of group.conversations; track conversation.id) {
              <li class="rounded-[10px]" [class.bg-status-primary-bg]="conversation.id === chat.conversationId()">
                @if (pendingDeleteId() === conversation.id) {
                  <div class="flex items-center gap-2 p-2 text-sm" role="alertdialog" [attr.aria-label]="labels.deleteConfirm">
                    <span class="flex-1">{{ labels.deleteConfirm }}</span>
                    <sk-button variant="danger" size="sm" (skClick)="confirmDelete(conversation.id)">{{ labels.confirmDelete }}</sk-button>
                    <sk-button variant="ghost" size="sm" (skClick)="pendingDeleteId.set(null)">{{ labels.cancel }}</sk-button>
                  </div>
                } @else {
                  <div class="flex items-center">
                    <button
                      type="button"
                      class="flex min-w-0 flex-1 flex-col items-start rounded-[10px] px-2.5 py-2 text-left hover:bg-sk-surface-hover"
                      (click)="openConversation(conversation.id)"
                    >
                      <span class="w-full truncate text-sm font-semibold">{{ conversation.title }}</span>
                      <span class="text-xs text-sk-text-secondary">{{ conversation.updatedAt | date: 'HH:mm' }} · {{ conversation.messages.length }} {{ conversation.messages.length === 1 ? 'mensaje' : 'mensajes' }}</span>
                    </button>
                    <sk-button
                      variant="ghost"
                      size="sm"
                      [attr.aria-label]="labels.deleteConversation + ': ' + conversation.title"
                      [attr.title]="labels.deleteConversation"
                      (skClick)="pendingDeleteId.set(conversation.id)"
                    >
                      <sk-icon name="trash" size="sm" />
                    </sk-button>
                  </div>
                }
              </li>
            }
          </ul>
        }
      </section>
    } @else {
      <div class="flex flex-1 flex-col gap-3.5 overflow-y-auto px-3.5 py-4" [attr.aria-busy]="chat.isStreaming()">
        @if (chat.messages().length === 0) {
          <div class="mt-3">
            <span class="grid h-9 w-9 place-items-center rounded-[10px] bg-status-primary-bg text-status-primary-fg" aria-hidden="true">
              <sk-icon name="bot" size="md" />
            </span>
            <h3 class="mb-1 mt-2.5 text-xl font-semibold">
              @if (firstName(); as name) {
                {{ labels.hello }}, {{ name }}
              } @else {
                {{ labels.hello }}
              }
            </h3>
            <p class="text-sk-text-secondary">{{ labels.intro }}</p>
          </div>
          <div class="mt-2 flex flex-col gap-2">
            @for (suggestion of labels.suggestions; track suggestion.title) {
              <button
                type="button"
                class="flex items-start gap-2.5 rounded-xl border border-sk-border bg-sk-surface px-3 py-2.5 text-left hover:border-sk-primary hover:bg-status-primary-bg disabled:cursor-not-allowed disabled:opacity-60"
                [disabled]="chat.isBusy() || !chat.isEnabled()"
                (click)="onSuggestion(suggestion.message)"
              >
                <span class="grid h-[30px] w-[30px] flex-none place-items-center rounded-lg bg-status-primary-bg text-status-primary-fg" aria-hidden="true">
                  <sk-icon [name]="suggestion.icon" size="sm" />
                </span>
                <span>
                  <span class="block font-semibold">{{ suggestion.title }}</span>
                  <span class="text-[12.5px] text-sk-text-secondary">{{ suggestion.hint }}</span>
                </span>
              </button>
            }
            @if (chat.canRecord()) {
              <button
                type="button"
                class="flex items-start gap-2.5 rounded-xl border border-sk-border bg-sk-surface px-3 py-2.5 text-left hover:border-sk-primary hover:bg-status-primary-bg"
                (click)="onRecordClick()"
              >
                <span class="grid h-[30px] w-[30px] flex-none place-items-center rounded-lg bg-status-primary-bg text-status-primary-fg" aria-hidden="true">
                  <sk-icon [name]="labels.recordSuggestion.icon" size="sm" />
                </span>
                <span>
                  <span class="block font-semibold">{{ labels.recordSuggestion.title }}</span>
                  <span class="text-[12.5px] text-sk-text-secondary">{{ labels.recordSuggestion.hint }}</span>
                </span>
              </button>
            }
          </div>
          <p class="mt-auto pt-3 text-center text-xs text-sk-text-secondary">{{ labels.disclaimer }}</p>
        }

        <ul class="flex flex-col gap-3.5">
          @for (message of chat.messages(); track message.id) {
            @if (message.role === 'user') {
              <li class="flex justify-end">
                <div class="max-w-[86%]">
                  <div class="rounded-2xl rounded-br-sm border border-status-primary-border bg-status-primary-bg px-3 py-2 text-sm text-status-primary-fg">
                    @if (message.content) {
                      <p class="whitespace-pre-wrap">{{ message.content }}</p>
                    }
                    @if (message.attachments?.length) {
                      <ul class="mt-1 flex flex-col gap-1" [attr.aria-label]="labels.attachedFiles">
                        @for (file of message.attachments; track $index) {
                          <li class="flex items-center gap-1.5 text-xs">
                            <sk-icon [name]="file.type.startsWith('image/') ? 'image' : 'file-text'" size="xs" />
                            <span class="truncate">{{ file.name }}</span>
                            <span class="opacity-80">{{ fileSize(file.size) }}</span>
                          </li>
                        }
                      </ul>
                    }
                  </div>
                  <p class="mt-0.5 text-right text-[11px] text-sk-text-secondary">{{ message.timestamp | date: 'HH:mm' }}</p>
                </div>
              </li>
            } @else {
              <li class="flex items-start gap-2">
                <span class="grid h-[26px] w-[26px] flex-none place-items-center rounded-lg bg-status-primary-bg text-status-primary-fg" aria-hidden="true">
                  <sk-icon name="bot" size="xs" />
                </span>
                <div class="min-w-0 flex-1 text-sm">
                  @if (message.sources?.length) {
                    <p class="mb-2 inline-flex items-center gap-2 rounded-full border border-sk-border bg-sk-surface-raised px-3 py-1.5 text-[12.5px] text-sk-text-secondary">
                      <sk-icon name="check" size="xs" class="text-status-success-fg" />
                      {{ sourcesLabel(message.sources?.length ?? 0) }}
                    </p>
                  }

                  @if (message.ticket; as ticket) {
                    <div class="flex items-start gap-2.5 rounded-xl border border-sk-border bg-sk-surface p-3">
                      <span class="grid h-[34px] w-[34px] flex-none place-items-center rounded-[9px] bg-status-success-bg text-status-success-fg" aria-hidden="true">
                        <sk-icon name="ticket-check" size="sm" />
                      </span>
                      <div class="min-w-0 flex-1">
                        <p class="font-mono text-[13px] text-status-primary-fg">{{ ticket.key }}</p>
                        @if (ticket.summary) {
                          <h4 class="text-sm font-semibold">{{ ticket.summary }}</h4>
                        }
                        @if (ticket.details) {
                          <p class="text-[12.5px] text-sk-text-secondary">{{ ticket.details }}</p>
                        }
                        <div class="mt-2 flex flex-wrap gap-2">
                          @if (ticket.url) {
                            <a
                              class="inline-flex items-center gap-1.5 rounded-lg border border-sk-primary bg-sk-primary px-2.5 py-1.5 text-[12.5px] font-semibold text-sk-text-inverse"
                              [href]="ticket.url"
                              target="_blank"
                              rel="noopener noreferrer"
                              >{{ labels.openInJira }}<sk-icon name="external-link" size="xs" /></a
                            >
                          }
                          <button
                            type="button"
                            class="rounded-lg border border-sk-border-strong px-2.5 py-1.5 text-[12.5px] font-semibold text-status-primary-fg hover:bg-sk-surface-hover"
                            (click)="copy('ticket:' + message.id, ticket.key)"
                          >
                            {{ copyLabel('ticket:' + message.id, labels.copyKey) }}
                          </button>
                        </div>
                      </div>
                    </div>
                    @if (ticket.warning) {
                      <p class="mt-2 flex items-start gap-2 rounded-[10px] border border-status-warning-border bg-status-warning-bg px-3 py-2 text-[12.5px] text-status-warning-fg">
                        <sk-icon name="triangle-alert" size="sm" />
                        <span>{{ ticket.warning }}</span>
                      </p>
                    }
                  } @else if (message.isError) {
                    <div class="flex items-start gap-2 rounded-[10px] border border-status-error-border bg-status-error-bg px-3 py-2 text-[12.5px] text-status-error-fg">
                      <sk-icon name="triangle-alert" size="sm" class="mt-0.5" />
                      <span class="flex-1">{{ message.content }}</span>
                      @if (message.id === lastMessageId() && chat.canRetry()) {
                        <sk-button variant="outline" size="sm" (skClick)="chat.retry()">{{ labels.retry }}</sk-button>
                      }
                    </div>
                  } @else {
                    @for (block of blocks(message); track $index) {
                      @switch (block.kind) {
                        @case ('heading') {
                          <p class="mb-1 font-semibold"><nf-leapbot-inlines [inlines]="block.inlines" /></p>
                        }
                        @case ('code') {
                          <pre class="my-1 overflow-x-auto rounded bg-sk-surface-sunken p-2 text-xs"><code>{{ block.code }}</code></pre>
                        }
                        @case ('list') {
                          @if (block.ordered) {
                            <ol class="my-1 list-decimal pl-5">
                              @for (item of block.items; track $index) {
                                <li class="mb-0.5"><nf-leapbot-inlines [inlines]="item" /></li>
                              }
                            </ol>
                          } @else {
                            <ul class="my-1 list-disc pl-5">
                              @for (item of block.items; track $index) {
                                <li class="mb-0.5"><nf-leapbot-inlines [inlines]="item" /></li>
                              }
                            </ul>
                          }
                        }
                        @default {
                          <p class="mb-2 last:mb-0"><nf-leapbot-inlines [inlines]="block.inlines" /></p>
                        }
                      }
                    }
                  }

                  @if (message.sources?.length) {
                    <div class="mt-2 flex flex-wrap items-center gap-1.5">
                      <span class="text-xs text-sk-text-secondary">{{ labels.sources }}</span>
                      @for (source of message.sources; track $index) {
                        @if (source.url) {
                          <a
                            class="inline-flex items-center gap-1 rounded-full border border-sk-border-strong bg-sk-surface px-2.5 py-0.5 text-xs text-status-primary-fg"
                            [href]="source.url"
                            target="_blank"
                            rel="noopener noreferrer"
                            >{{ source.title }}<sk-icon name="external-link" size="xs" /></a
                          >
                        } @else {
                          <span class="inline-flex rounded-full border border-sk-border-strong px-2.5 py-0.5 text-xs text-sk-text-secondary">{{ source.title }}</span>
                        }
                      }
                    </div>
                  }

                  @if (message.isTruncated) {
                    <p class="mt-2 flex items-center gap-2 rounded-[10px] border border-status-warning-border bg-status-warning-bg px-3 py-2 text-[12.5px] text-status-warning-fg">
                      <sk-icon name="triangle-alert" size="sm" />
                      <span class="flex-1">{{ labels.truncated }}</span>
                      @if (message.id === lastMessageId() && chat.canContinue()) {
                        <sk-button variant="outline" size="sm" (skClick)="chat.continueAnswer()">{{ labels.continue }}</sk-button>
                      }
                    </p>
                  }

                  <div class="mt-1 flex items-center gap-2 text-[11px] text-sk-text-secondary">
                    <span>{{ message.timestamp | date: 'HH:mm' }}</span>
                    @if (message.isComplete && !message.isError && !message.ticket && message.content) {
                      <button
                        type="button"
                        class="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs hover:bg-sk-surface-hover hover:text-sk-text"
                        (click)="copy(message.id, message.content)"
                      >
                        <sk-icon name="copy" size="xs" />{{ copyLabel(message.id, labels.copy) }}
                      </button>
                    }
                  </div>
                </div>
              </li>
            }
          }
        </ul>

        @if (chat.activeTool(); as tool) {
          <div class="flex items-start gap-2" role="status">
            <span class="grid h-[26px] w-[26px] flex-none place-items-center rounded-lg bg-status-primary-bg text-status-primary-fg" aria-hidden="true">
              <sk-icon name="bot" size="xs" />
            </span>
            <p class="inline-flex items-center gap-2 rounded-full border border-sk-border bg-sk-surface-raised px-3 py-1.5 text-[12.5px] text-sk-text-secondary">
              <span class="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-sk-border-strong border-t-sk-primary" aria-hidden="true"></span>
              {{ toolLabel(tool) }}
            </p>
          </div>
        } @else if (chat.isWaitingForAnswer()) {
          <div class="flex items-start gap-2" role="status">
            <span class="grid h-[26px] w-[26px] flex-none place-items-center rounded-lg bg-status-primary-bg text-status-primary-fg" aria-hidden="true">
              <sk-icon name="bot" size="xs" />
            </span>
            <span class="inline-flex gap-1 py-2" aria-hidden="true">
              <span class="h-1.5 w-1.5 animate-pulse rounded-full bg-sk-text-secondary"></span>
              <span class="h-1.5 w-1.5 animate-pulse rounded-full bg-sk-text-secondary" style="animation-delay: 0.15s"></span>
              <span class="h-1.5 w-1.5 animate-pulse rounded-full bg-sk-text-secondary" style="animation-delay: 0.3s"></span>
            </span>
            <span class="sr-only">{{ labels.typing }}</span>
          </div>
        }

        @if (chat.actionCard(); as card) {
          <div class="rounded-xl border border-status-warning-border bg-status-warning-bg p-3 text-sm text-status-warning-fg">
            <div class="flex items-start gap-2.5">
              <sk-icon name="lock" size="sm" class="mt-0.5" />
              <div>
                <p class="font-semibold">{{ card.title }}</p>
                <p class="mt-0.5 text-[12.5px]">{{ card.message }}</p>
              </div>
            </div>
            <div class="mt-2.5 flex flex-wrap gap-2">
              @if (card.actionUrl) {
                <a
                  class="inline-flex items-center gap-1.5 rounded-lg border border-sk-primary bg-sk-primary px-3 py-1.5 text-sm font-semibold text-sk-text-inverse"
                  [href]="card.actionUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  (click)="onAuthorize()"
                  >{{ card.actionLabel }}<sk-icon name="external-link" size="xs" /></a
                >
              }
              @if (card.isJiraAuth && authorizeClicked()) {
                <sk-button variant="outline" size="sm" (skClick)="onJiraAuthorized()">{{ labels.authorizedJira }}</sk-button>
              }
            </div>
          </div>
        }
      </div>

      <div class="sr-only" aria-live="polite" aria-atomic="true">{{ announcement() }}</div>

      <form class="border-t border-sk-border bg-sk-surface px-3 pb-3 pt-2.5" (submit)="$event.preventDefault(); onSend()">
        @if (chat.isRecording()) {
          <div class="mb-2 flex items-center gap-2.5 rounded-xl border border-status-error-border bg-status-error-bg px-3 py-2 font-semibold text-status-error-fg" role="status">
            <span class="h-2.5 w-2.5 animate-pulse rounded-full bg-status-error-fg" aria-hidden="true"></span>
            <span>{{ labels.recording }} {{ elapsedLabel() }}</span>
            <sk-button class="ml-auto" variant="danger" size="sm" [attr.aria-label]="labels.stopRecording" (skClick)="onStopRecording()">
              <sk-icon name="square" size="xs" />{{ labels.stopRecordingShort }}
            </sk-button>
          </div>
        }
        @if (showRecordNotice()) {
          <div class="mb-2 rounded-xl border border-sk-border bg-sk-surface-raised p-3 text-[12.5px]" role="group" [attr.aria-label]="labels.recordNoticeLabel">
            <p>{{ labels.recordNotice }}</p>
            <div class="mt-2 flex gap-2">
              <sk-button variant="primary" size="sm" (skClick)="confirmRecord()">{{ labels.startRecording }}</sk-button>
              <sk-button variant="ghost" size="sm" (skClick)="showRecordNotice.set(false)">{{ labels.cancel }}</sk-button>
            </div>
          </div>
        }
        @if (chat.attachmentNotice(); as notice) {
          <p class="mb-2 rounded-[10px] border border-status-error-border bg-status-error-bg px-3 py-1.5 text-xs text-status-error-fg" role="alert">{{ notice }}</p>
        }
        @if (chat.attachments().length > 0) {
          <ul class="mb-2 flex flex-wrap gap-1.5" [attr.aria-label]="labels.attachedFiles">
            @for (attachment of chat.attachments(); track attachment.id) {
              <li class="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-sk-border-strong bg-sk-surface-raised py-1 pl-2 pr-1 text-xs">
                <sk-icon [name]="attachment.type.startsWith('image/') ? 'image' : 'file-text'" size="xs" />
                @if (canPreview(attachment)) {
                  <button
                    type="button"
                    class="max-w-[10rem] truncate hover:underline"
                    [attr.aria-label]="labels.viewFile + ' ' + attachment.name"
                    (click)="openPreview(attachment)"
                  >
                    {{ attachment.name }}
                  </button>
                } @else {
                  <span class="max-w-[10rem] truncate">{{ attachment.name }}</span>
                }
                <span class="text-sk-text-secondary">{{ fileSize(attachment.size) }}</span>
                <button
                  type="button"
                  class="grid h-5 w-5 place-items-center rounded text-sk-text-secondary hover:bg-sk-surface-hover hover:text-sk-text"
                  [attr.aria-label]="labels.removeFile + ' ' + attachment.name"
                  (click)="chat.removeAttachment(attachment.id)"
                >
                  <sk-icon name="x" size="xs" />
                </button>
              </li>
            }
          </ul>
        }
        <div class="rounded-2xl border border-sk-border-strong bg-sk-surface py-2 pl-3 pr-2 focus-within:border-sk-primary focus-within:ring-2 focus-within:ring-sk-primary/20">
          <textarea
            #messageInput
            rows="2"
            class="min-h-[2.5rem] w-full resize-none border-0 bg-transparent p-0 text-sm text-sk-text outline-none"
            [formControl]="messageControl"
            [attr.aria-label]="labels.inputLabel"
            [placeholder]="labels.placeholder"
            (keydown)="onKeydown($event)"
            (paste)="onPaste($event)"
          ></textarea>
          <input #fileInput type="file" multiple class="hidden" tabindex="-1" aria-hidden="true" [accept]="fileAccept" (change)="onFilesSelected($event)" />
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-0.5">
            <sk-button variant="ghost" size="sm" [disabled]="!chat.isEnabled()" [attr.aria-label]="labels.attach" [attr.title]="labels.attach" (skClick)="openFilePicker()">
              <sk-icon name="paperclip" size="sm" />
            </sk-button>
            <sk-button
              variant="ghost"
              size="sm"
              [disabled]="!chat.canRecord()"
              [attr.aria-label]="labels.recordScreen"
              [attr.title]="chat.recordDisabledReason() ?? labels.recordScreen"
              (skClick)="onRecordClick()"
            >
              <sk-icon name="video" size="sm" />
            </sk-button>
            </div>
            @if (chat.isBusy()) {
              <sk-button variant="secondary" size="md" [attr.aria-label]="labels.stop" [attr.title]="labels.stop" (skClick)="chat.stop()">
                <sk-icon name="square" size="sm" />
              </sk-button>
            } @else {
              <sk-button variant="primary" size="md" [disabled]="!canSend()" [attr.aria-label]="labels.send" [attr.title]="labels.send" (skClick)="onSend()">
                <sk-icon name="send" size="sm" />
              </sk-button>
            }
          </div>
        </div>
        <p class="mx-1 mt-1.5 text-[11.5px] text-sk-text-secondary">{{ labels.composerHint }}</p>
      </form>
    }
    @if (preview(); as p) {
      <div class="fixed inset-0 z-[70] flex items-center justify-center bg-[var(--sk-color-surface-overlay)] p-4" (click)="closePreview()">
        <div
          class="flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-sk-border bg-sk-surface text-sk-text shadow-xl"
          role="dialog"
          aria-modal="true"
          [attr.aria-label]="labels.previewTitle + ': ' + p.attachment.name"
          (click)="$event.stopPropagation()"
        >
          <div class="flex items-center gap-2 border-b border-sk-border px-4 py-2.5">
            <h3 class="min-w-0 flex-1 truncate text-sm font-semibold">{{ p.attachment.name }}</h3>
            <span class="text-xs text-sk-text-secondary">{{ fileSize(p.attachment.size) }}</span>
            <button
              #previewClose
              type="button"
              class="grid h-8 w-8 place-items-center rounded-lg text-sk-text-secondary hover:bg-sk-surface-hover hover:text-sk-text"
              [attr.aria-label]="labels.closePreview"
              (click)="closePreview()"
            >
              <sk-icon name="x" size="sm" />
            </button>
          </div>
          <div class="overflow-auto p-4">
            @switch (p.kind) {
              @case ('image') {
                <img class="mx-auto max-h-[70vh] max-w-full" [src]="p.url" [alt]="p.attachment.name" />
              }
              @case ('video') {
                <video class="mx-auto max-h-[70vh] max-w-full" [src]="p.url" controls></video>
              }
              @default {
                <pre class="whitespace-pre-wrap break-words text-xs">{{ p.text }}</pre>
              }
            }
          </div>
        </div>
      </div>
    }
  </aside>
}
